import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import NavigationBar from '../../components/ui/NavigationBar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { useAuth } from '../../contexts/AuthContext';
import UserAnalyticsKPIs from './components/UserAnalyticsKPIs';
import OnboardingFunnel from './components/OnboardingFunnel';
import CohortRetention from './components/CohortRetention';
import FeatureAdoption from './components/FeatureAdoption';
import ChurnSignals from './components/ChurnSignals';
import UserSegments from './components/UserSegments';

const kpis = [
  {
    label: 'Total Active Users',
    value: '1,238',
    change: '+12.4%',
    changeType: 'positive',
    icon: 'Users',
    color: '#107570',
    sub: 'vs 1,102 last month',
  },
  {
    label: 'Onboarding Completion',
    value: '15%',
    change: '+4.2%',
    changeType: 'positive',
    icon: 'CheckCircle',
    color: '#0d9488',
    sub: '186 fully onboarded this month',
  },
  {
    label: 'Monthly Churn Rate',
    value: '1.9%',
    change: '-0.5%',
    changeType: 'positive',
    icon: 'UserMinus',
    color: '#ef4444',
    sub: '21 users churned in Jul',
  },
  {
    label: 'Avg. Feature Adoption',
    value: '40%',
    change: '+2.1%',
    changeType: 'positive',
    icon: 'Layers',
    color: '#14b8a6',
    sub: 'Across 8 core features',
  },
];

const UserAnalyticsDashboard = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [timeRange, setTimeRange] = useState('30d');
  const [lastRefreshed, setLastRefreshed] = useState(new Date());
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/authentication');
    }
  }, [user, authLoading, navigate]);

  const handleRefresh = () => {
    setRefreshing(true);
    setTimeout(() => {
      setLastRefreshed(new Date());
      setRefreshing(false);
    }, 800);
  };

  const timeRanges = [
    { label: '7D', value: '7d' },
    { label: '30D', value: '30d' },
    { label: '90D', value: '90d' },
    { label: '1Y', value: '1y' },
  ];

  if (authLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex items-center gap-3 text-muted-foreground">
          <Icon name="Loader2" size={20} className="animate-spin" />
          <span className="text-sm">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <NavigationBar stats={kpis} />
      <div
        className="pt-16 transition-all duration-300"
        style={{ marginLeft: 'var(--sidebar-width, 256px)' }}
      >
        <div className="p-6 max-w-[1400px]">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Icon name="Users" size={18} color="#107570" />
                </div>
                <h1 className="text-xl font-bold text-foreground">User Analytics</h1>
              </div>
              <p className="text-sm text-muted-foreground">
                Onboarding, feature adoption, churn signals, and cohort retention
              </p>
            </div>

            <div className="flex items-center gap-3 flex-wrap">
              {/* Time range selector */}
              <div className="flex items-center gap-1 bg-muted rounded-lg p-1">
                {timeRanges?.map(t => (
                  <button
                    key={t?.value}
                    onClick={() => setTimeRange(t?.value)}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                      timeRange === t?.value
                        ? 'bg-card text-foreground shadow-sm'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {t?.label}
                  </button>
                ))}
              </div>

              <Button
                variant="outline"
                size="sm"
                iconName={refreshing ? 'Loader2' : 'RefreshCw'}
                onClick={handleRefresh}
                disabled={refreshing}
              >
                {refreshing ? 'Refreshing...' : 'Refresh'}
              </Button>
            </div>
          </div>

          {/* Last refreshed */}
          <div className="flex items-center gap-1.5 mb-5">
            <div className="w-1.5 h-1.5 rounded-full bg-success animate-pulse" />
            <span className="text-xs text-muted-foreground">
              Last updated {lastRefreshed?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </span>
          </div>

          {/* KPI Cards */}
          <div className="mb-6">
            <UserAnalyticsKPIs kpis={kpis} />
          </div>

          {/* Onboarding Funnel + Churn Signals */}
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-5 mb-5">
            <OnboardingFunnel />
            <ChurnSignals />
          </div>

          {/* Cohort Retention (full width) */}
          <div className="mb-5">
            <CohortRetention />
          </div>

          {/* Feature Adoption (full width) */}
          <div className="mb-5">
            <FeatureAdoption />
          </div>

          {/* User Segments + Growth */}
          <div className="mb-5">
            <UserSegments />
          </div>

          {/* Footer note */}
          <p className="text-xs text-muted-foreground text-center pb-4">
            Data shown is representative. Connect your analytics pipeline to display live metrics.
          </p>
        </div>
      </div>
    </div>
  );
};

export default UserAnalyticsDashboard;
